import { differenceInDays } from 'date-fns';

import type {
  Trip,
  BookingStatusMessage,
  CancellationMessage,
  ModificationMessage,
  ReimbursementMessage,
} from '~types';
import {
  BookingProductStatus,
  BookingStatus,
  TripType,
  MessageType,
  MessageStatus,
} from '~types';
import { mapAirportToCity } from '~touchpoints/AccommodationRecommendations/utils/mapAirportToCity';
import { addDaysToTimestamp } from './dateUtils';
import type { CheckoutSegment } from '~touchpoints/HotelDealsMobileCard/types';

export type TripMessage =
  | CancellationMessage
  | ReimbursementMessage
  | BookingStatusMessage
  | ModificationMessage;

export const MAX_DAYS_BETWEEN_CHECKIN_AND_CHECKOUT = 28;

export const getNumberOfPassengers = (trip: Trip): number => {
  return trip.travellers.length;
};

export const getDestinationCityName = (trip: Trip): string | null => {
  return trip.itinerary?.destination?.cityName || null;
};

export const getDestinationId = (trip: Trip): number | null => {
  const geoNodeId = trip.itinerary?.destination?.id;
  const formattedId = geoNodeId ? mapAirportToCity(Number(geoNodeId)) : null;
  return formattedId?.id || null;
};

export const getDestinationArrivalDate = (trip: Trip): number | null => {
  const firstLeg = trip.itinerary?.legs[0];
  if (!firstLeg) {
    return null;
  }
  const lastSection = firstLeg.sections[firstLeg.sections.length - 1];
  return lastSection.arrivalDate;
};

export const getDestinationDepartureDate = (trip: Trip): number | null => {
  const secondLeg = trip.itinerary?.legs[1];
  if (!secondLeg) {
    return null;
  }
  const firstSection = secondLeg.sections[0];
  return firstSection.departureDate;
};

export const getTripType = (trip: Trip): TripType | null => {
  const tripType = trip.itinerary?.tripType;
  if (!tripType) {
    return null;
  }
  return tripType;
};

export const isTripConfirmed = (trip: Trip): boolean =>
  trip.bookingStatus === BookingStatus.Contract &&
  trip.bookingProductStatus === BookingProductStatus.Confirmed;

export const hasAccommodationOnTrip = (trip: Trip): boolean =>
  !!(trip.accommodationBooking || trip.accommodationProductBooking);

export const isMultipleDestinationsTrip = (trip: Trip): boolean => {
  return getTripType(trip) === TripType.MultipleDestinations;
};

export const isTripMessageConfirmed = (tripMessage: TripMessage): boolean =>
  tripMessage.type === MessageType.BookingStatus &&
  tripMessage.status === MessageStatus.Confirmed;

export const getCheckoutDateForRoundTrip = (trip: Trip): number | null => {
  const destinationArrivalDate: number | null = getDestinationArrivalDate(trip);

  if (destinationArrivalDate === null) {
    return null;
  }

  const destinationDepartureDate: number | null =
    getDestinationDepartureDate(trip);

  if (destinationDepartureDate === null) {
    return null;
  }

  return calculateCheckoutDateWithMaxStay(
    destinationDepartureDate,
    destinationArrivalDate
  );
};

const calculateCheckoutDateWithMaxStay = (
  departureDate: number,
  arrivalDate: number
): number => {
  if (
    differenceInDays(departureDate, arrivalDate) >
    MAX_DAYS_BETWEEN_CHECKIN_AND_CHECKOUT
  ) {
    return addDaysToTimestamp(
      arrivalDate,
      MAX_DAYS_BETWEEN_CHECKIN_AND_CHECKOUT
    );
  }

  return departureDate;
};

export const getCheckoutDateForRoundTripFromSegments = (
  segments: CheckoutSegment[]
): number | null => {
  const [firstSegment, secondSegment] = segments;

  if (
    segments.length < 2 ||
    !firstSegment.arrivalDate ||
    !secondSegment.departureDate
  ) {
    return null;
  }

  const arrivalDate = new Date(firstSegment.arrivalDate).getTime();
  const departureDate = new Date(secondSegment.departureDate).getTime();

  return calculateCheckoutDateWithMaxStay(departureDate, arrivalDate);
};

const getCheckoutDateForOneWayTripGeneric = (arrivalDate: number): number => {
  return addDaysToTimestamp(arrivalDate, 2);
};

export const getCheckoutDateForOneWayTrip = (trip: Trip): number | null => {
  const destinationArrivalDate: number | null = getDestinationArrivalDate(trip);

  if (destinationArrivalDate === null) {
    return null;
  }

  return getCheckoutDateForOneWayTripGeneric(destinationArrivalDate);
};

export const getCheckoutDateForOneWayTripFromSegments = (
  segments: CheckoutSegment[]
): number | null => {
  if (segments.length < 1) {
    return null;
  }

  const [firstSegment] = segments;

  if (!firstSegment.arrivalDate) {
    return null;
  }

  return getCheckoutDateForOneWayTripGeneric(
    new Date(firstSegment.arrivalDate).getTime()
  );
};

export const getCheckinDateFromSegments = (
  segments: CheckoutSegment[]
): number | null => {
  if (segments.length < 1) {
    return null;
  }

  const [firstSegment] = segments;

  if (!firstSegment.arrivalDate) {
    return null;
  }

  return new Date(firstSegment.arrivalDate).getTime();
};
